import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../api/client';
import { ReviewQueueItem } from '../types';
import Spinner from '../components/Spinner';
import Badge from '../components/Badge';
import ContextViewer from '../components/ContextViewer';

const priorityVariant: Record<string, 'danger' | 'warning' | 'default'> = {
  high: 'danger',
  normal: 'warning',
  low: 'default',
};

export default function DecisionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<ReviewQueueItem | null>(null);
  const [selectedOption, setSelectedOption] = useState('');
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ data: ReviewQueueItem }>(`/review-queue/${id}`)
      .then((res) => setItem(res.data))
      .catch((err) => setError(err.message));
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOption) return;

    setSubmitting(true);
    try {
      const volunteer = JSON.parse(localStorage.getItem('varilib_volunteer') || '{}');
      await api.post(`/review-queue/${id}/decide`, {
        decided_by: volunteer.id,
        decision: selectedOption,
        rationale,
      });
      navigate('/review');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (error && !item) return <p className="error">{error}</p>;
  if (!item) return <Spinner text="Loading review item..." />;

  const isResolved = item.status === 'resolved';

  return (
    <div>
      <div style={{ marginBottom: '0.5rem' }}>
        <Link to="/review">&larr; Back to Review Queue</Link>
      </div>

      <h1>Review Item #{item.id}</h1>

      <div className="card">
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <Badge label={item.priority} variant={priorityVariant[item.priority]} />
          <Badge label={item.entity_type} />
          <Badge label={item.status} variant={item.status === 'resolved' ? 'success' : 'info'} />
        </div>

        <h2>{item.question}</h2>

        {item.context_json && (
          <div style={{ marginTop: '1rem' }}>
            <h3>Context</h3>
            <ContextViewer data={item.context_json as Record<string, unknown>} />
          </div>
        )}
      </div>

      {isResolved ? (
        <div className="card">
          <p className="text-muted">This item has been resolved.</p>
        </div>
      ) : (
        <div className="card">
          <h2>Your Decision</h2>
          {error && <p className="error">{error}</p>}

          <form onSubmit={handleSubmit}>
            <fieldset>
              <legend>Choose an option</legend>
              {(item.options_json || []).map((option) => (
                <label key={option} className="radio-option">
                  <input
                    type="radio"
                    name="decision"
                    value={option}
                    checked={selectedOption === option}
                    onChange={(e) => setSelectedOption(e.target.value)}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </fieldset>

            <label style={{ marginTop: '1rem' }}>
              Rationale (optional)
              <textarea
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
                rows={4}
                placeholder="Explain your reasoning..."
              />
            </label>

            <div style={{ marginTop: '1rem' }}>
              <button type="submit" disabled={submitting || !selectedOption}>
                {submitting ? 'Submitting...' : 'Submit Decision'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
